import {Box, Card, Flex, FormControl, FormLabel, Heading, Image, Input, Stack, Text} from '@chakra-ui/react';
import React, {useEffect, useRef, useState} from 'react';
import {useVuplex} from '../../hooks/useVuplex';
import PageTitle from './PageTitle';
import {sendMessage} from '../../vuplex';
import _ from 'lodash';
import ButtonMinor from '../../components/ButtonMinor';
import {MdPlayArrow, MdStop} from 'react-icons/md';
import {useEnterKey} from '../../hooks/useEnterKey';
import InAppTooltip from './UnityReactComponents/InAppTooltip';

const sendInitRequest = () => sendMessage({
  Type: 'drumMachineInitRequest',
});

const sendAuditionRequest = ({sampleName}) => sendMessage({
  Type: 'drumMachineAudition',
  SampleName: sampleName,
});

const sendUiState = ({bpm, beatCount, isPlaying}) => sendMessage({
  Type: 'drumMachineState',
  BeatCount: beatCount,
  Bpm: bpm,
  Playing: isPlaying,
});

const sendButtonState = ({buttonId, beat, velocity, sampleName}) => sendMessage({
  Type: 'drumMachineButtonState',
  Beat: beat,
  ButtonId: buttonId,
  SampleName: sampleName,
  Velocity: velocity,
});


const IntegerSelect = ({
  label,
  max,
  min,
  step,
  onChange,
  inputWidth,
  value = 0,
}) => {
  const [formValue, setFormValue] = useState(value);

  const updateValue = (n) => {
    const val = Math.max(Math.min(n, max), min);
    onChange(val);
  };

  useEffect(() => {
    setFormValue(value);
  }, [value]);

  return (
    <FormControl display="flex" gap={1} alignItems="center" w="unset">
      <Text color="yellow" fontSize="2em" fontFamily="monospace">{label}</Text>
      <Input
        color="cyan"
        bg="darkcyan"
        fontSize="2em"
        fontFamily="monospace"
        p={2}
        textAlign="right"
        type="number"
        max={max}
        min={min}
        step={step || 1}
        value={formValue}
        w={inputWidth}
        flexShrink={0}
        onChange={(e) => {
          setFormValue(parseInt(e.target.value, 10));
        }}
        onBlur={(e) => {
          updateValue(formValue);
        }}
      />
      <Flex gap={1}>
        <ButtonMinor
          bg="darkmagenta" color="pink"
          isDisabled={formValue <= min} onClick={() => updateValue(formValue - (step || 1))}
                     borderRadius="50% 0 0 50%">-</ButtonMinor>
        <ButtonMinor
          bg="darkmagenta" color="pink"
          isDisabled={formValue >= max} onClick={() => updateValue(formValue + (step || 1))}
                     borderRadius="0 50% 50% 0">+</ButtonMinor>
      </Flex>
    </FormControl>
  );
};


const DrumMachineButton = ({
  beat,
  buttonId,
  currentBeat,
  sampleName,
  velocity,
}) => {

  const isPlaying = currentBeat === beat && velocity > 0;

  return (
    <Box
      bg={isPlaying
        ? 'rgb(100, 255, 100)'
        : velocity === 0
          ? '#0009'
          : `rgb(10, ${velocity * 255}, 10)`}
      border={currentBeat === beat ? '2px solid rgb(100, 255, 100)' : '2px solid black'}
      onClick={() => {
        sendButtonState({
          buttonId,
          beat,
          velocity: velocity === 0
            ? 1
            : velocity === 1
              ? 0.5
              : 0,
          sampleName,
        });
      }}
      h="full"
      w="full"
    />
  );
};

const DrumMachine = ({}) => {
  const [drumMachineId, setDrumMachineId] = useState('');
  const [currentBeat, setCurrentBeat] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [beatCount, setBeatCount] = useState(16);
  const [bpm, setBpm] = useState(120);
  const [buttonVelocities, setButtonVelocities] = useState({});

  useEnterKey();

  const intervalId = useRef(null);

  useEffect(() => {
    document.body.style.background = 'none';

    if (drumMachineId) {
      clearInterval(intervalId.current);
      return;
    }

    sendInitRequest();

    intervalId.current = setInterval(() => {
      sendInitRequest();
    }, 1000)
  }, [drumMachineId]);

  useVuplex({
    drumMachineInit: ({DrumMachineId}) => {
      // console.log('drumMachineInit', DrumMachineId);
      setDrumMachineId(DrumMachineId);
      setButtonVelocities({});
    },

    drumMachineBeat: ({Beat}) => {
      // console.log('drumMachineBeat', Beat);
      setCurrentBeat(Beat);
    },

    drumMachineUiState: ({BeatCount, Bpm, Playing}) => {
      // console.log('drumMachineUiState', BeatCount, Bpm, Playing);
      if (BeatCount === 0 || Bpm === 0) return; // ignore default model values

      setBeatCount(BeatCount);
      setBpm(Bpm);
      setIsPlaying(Playing);
    },

    drumMachineButtonState: ({Beat, ButtonId, SampleName, Velocity}) => {
      setButtonVelocities(prev => ({...prev, [ButtonId]: Velocity}));
    },
  });


  const {sampleNames} = {
    RolandHeadFactory: {
      sampleNames: [
        'kick',
        'snare',
        'rimshot',
        'closedHiHat',
        'openHiHat',
        'tomHi',
        'tomMid',
        'tomLow',
        'cymbal',
        'clap',
        'cowbell',
        'claves',
        'maracas',
      ],
    },
    Clank1: {
      sampleNames: [
        'kick',
        'snare',
        'rimshot',
        'closedHiHat',
        'openHiHat',
        'cowbell',
        'tomHi',
      ],
    },
    Clank2: {
      sampleNames: [
        'tomMid',
        'tomLow',
        'cymbal',
        'clap',
        'claves',
        'maracas',
      ],
    },
  }[drumMachineId] || {
    sampleNames: [],
  };

  return (
    <>
      <PageTitle title="DrumMachine - Wooorld"/>

      <Stack
        h="100vh"
        w="100vw"
        // bg="cyan"
        gap={1}
      >
        <Card bg="grey" p={2} marginBottom={2}>
          <Flex justifyContent="space-between" w="full">
            <ButtonMinor
              bg="darkmagenta" color="pink"
              onClick={() => {
                sendUiState({
                  beatCount,
                  bpm,
                  isPlaying: !isPlaying,
                });
              }}
            >
              {isPlaying ? <MdStop/> : <MdPlayArrow/>}
            </ButtonMinor>

            <IntegerSelect
              label="Beats"
              max={64}
              min={2}
              onChange={(n) => {
                sendUiState({
                  beatCount: n,
                  bpm,
                  isPlaying,
                });
              }}
              value={beatCount}
              inputWidth="2.5em"
            />

            <IntegerSelect
              label="BPM"
              max={240}
              min={15}
              step={5}
              onChange={(n) => {
                sendUiState({
                  beatCount,
                  bpm: n,
                  isPlaying,
                });
              }}
              value={bpm}
              inputWidth="3.5em"
            />
          </Flex>

        </Card>

        {sampleNames.map((sampleName, i) => (
          <Flex key={sampleName} gap="1px" align="center">
            <Box
              bg="#6669"
              flexShrink={0}
              p={0.25}
              onClick={() => {
                sendAuditionRequest({sampleName});
              }}
            >
              <InAppTooltip message="click to audition<br>(only you can hear it)">

                <Image
                  filter="drop-shadow(1px 2px 2px limegreen) brightness(0.7)"

                  src={`drumMachineIcons/${sampleName}.png`}
                  alt={sampleName}
                  h="3em"
                  w="2.5em"
                  objectFit="contain"
                  objectPosition="center"
                />
              </InAppTooltip>
            </Box>


            {_.range(beatCount).map((beat) => {
              const buttonId = `${sampleName}-${beat}`;
              return (
                <DrumMachineButton
                  beat={beat}
                  buttonId={buttonId}
                  currentBeat={currentBeat}
                  key={buttonId}
                  sampleName={sampleName}
                  velocity={buttonVelocities[buttonId] || 0}
                />
              );
            })}
          </Flex>
        ))}
      </Stack>
    </>
  );


};

export default DrumMachine;
